import * as B from '@blueprintjs/core';
import moment from 'moment';
import React from 'react';

import {useAuth} from 'app/providers/AuthProvider';
import {useOrgUsers} from 'app/providers/OrgUsersProvider';

import {useArchiveExploreRun, useExploreRunsQuery} from '../hooks';
import cs from './RunsView.module.scss';
import {getRunTitle} from '../PromptView/promptConfigs';
import {ApiExploreRun} from '../types';

interface Props {
  onSelectRun: (run: ApiExploreRun) => void;
  featureId: number;
}

type RunFilter = 'all' | 'mine';
type RunSort = 'newest-first' | 'oldest-first' | 'observations';

const PER_PAGE = 10;

const SORT_ICONS: Record<RunSort, B.IconName> = {
  'newest-first': 'sort-desc',
  'oldest-first': 'sort-asc',
  observations: 'horizontal-bar-chart-desc',
};

// Dismissed (archived) notes don't count as observations.
const getObservationCount = (run: ApiExploreRun) =>
  run.notes.filter((note) => !note.isArchived).length;

const RunsView: React.FC<Props> = ({onSelectRun, featureId}) => {
  const [filter, setFilter] = React.useState<RunFilter>('all');
  const [sort, setSort] = React.useState<RunSort>('newest-first');
  const auth = useAuth();
  const currentUserId = auth.status === 'logged in' ? auth.profile?.get('id') : undefined;
  const {organizationUsers} = useOrgUsers();

  const [currentPage, setCurrentPage] = React.useState(1);
  const {data: exploreRuns, isFetching} = useExploreRunsQuery(featureId);
  const archiveRun = useArchiveExploreRun(featureId);

  const filteredRuns =
    exploreRuns &&
    (filter === 'mine'
      ? exploreRuns.filter((run) => run.createdBy === currentUserId)
      : exploreRuns);

  const sortedAndFilteredRuns =
    filteredRuns &&
    [...filteredRuns].sort((a, b) => {
      switch (sort) {
        case 'newest-first':
          return moment(b.startedAt).diff(moment(a.startedAt));
        case 'oldest-first':
          return moment(a.startedAt).diff(moment(b.startedAt));
        case 'observations':
          return getObservationCount(b) - getObservationCount(a);
        default:
          return 0;
      }
    });

  const totalPages = sortedAndFilteredRuns ? Math.ceil(sortedAndFilteredRuns.length / PER_PAGE) : 0;
  // Clamp to valid range — currentPage can overshoot when filtering reduces results
  // or archiving a run removes the last item on the current page
  const effectivePage = Math.max(1, Math.min(currentPage, totalPages || 1));
  const paginatedRuns = sortedAndFilteredRuns?.slice(
    (effectivePage - 1) * PER_PAGE,
    effectivePage * PER_PAGE
  );

  const isFilterActive = filter !== 'all';

  if (isFetching && !exploreRuns) {
    return (
      <div className={cs.pastRunsSection}>
        <B.Spinner />
      </div>
    );
  }

  return (
    <div className={cs.pastRunsSection}>
      {exploreRuns && exploreRuns.length > 0 && (
        <div className={cs.toolbar}>
          <B.Popover
            interactionKind={B.PopoverInteractionKind.CLICK}
            content={
              <>
                <div className={cs.popoverHeader}>
                  <div className={cs.popoverHeaderTitle}>Sort</div>
                </div>
                <div className={cs.sortsList}>
                  <B.RadioGroup
                    selectedValue={sort}
                    onChange={(e) => {
                      setSort(e.currentTarget.value as RunSort);
                      setCurrentPage(1);
                    }}
                  >
                    <B.Radio label="Newest first" value="newest-first" />
                    <B.Radio label="Oldest first" value="oldest-first" />
                    <B.Radio label="Most observations" value="observations" />
                  </B.RadioGroup>
                </div>
              </>
            }
          >
            <B.Tooltip content="Sort">
              <B.AnchorButton icon={SORT_ICONS[sort]} minimal />
            </B.Tooltip>
          </B.Popover>
          <B.Popover
            interactionKind={B.PopoverInteractionKind.CLICK}
            content={
              <>
                <div className={cs.popoverHeader}>
                  <div className={cs.popoverHeaderTitle}>Filter</div>
                </div>
                <div className={cs.filtersBody}>
                  <B.RadioGroup
                    selectedValue={filter}
                    onChange={(e) => {
                      setFilter(e.currentTarget.value as RunFilter);
                      setCurrentPage(1);
                    }}
                  >
                    <B.Radio label="All runs" value="all" />
                    <B.Radio label="My runs" value="mine" />
                  </B.RadioGroup>
                </div>
              </>
            }
          >
            <B.Tooltip content="Filter">
              <B.AnchorButton icon="filter" minimal active={isFilterActive} />
            </B.Tooltip>
          </B.Popover>
        </div>
      )}
      <div className={cs.pastRunsList}>
        {paginatedRuns &&
          (sortedAndFilteredRuns && sortedAndFilteredRuns.length === 0 ? (
            <B.NonIdealState
              icon="path-search"
              iconMuted={false}
              description='Ask a question about your property in the "Prompt" tab.'
              className={cs.noRunsState}
            />
          ) : (
            paginatedRuns.map((run) => {
              const creator = organizationUsers?.find((u) => u.id === run.createdBy);
              const creatorLabel = creator ? creator.name || creator.email : null;
              const isArchiving =
                archiveRun.isPending && archiveRun.variables?.agentRunId === run.id;
              return (
                // A div (not a button) so the nested archive button is valid HTML.
                <div
                  key={run.id}
                  role="button"
                  tabIndex={0}
                  className={cs.pastRunCard}
                  onClick={() => !isArchiving && onSelectRun(run)}
                  onKeyDown={(e) => {
                    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      if (!isArchiving) onSelectRun(run);
                    }
                  }}
                >
                  <div className={cs.runInfo}>
                    <div className={cs.runTitle}>{getRunTitle(run)}</div>
                    <div className={cs.runMeta}>
                      {/* The name yields (ellipsis) before the timestamp ever gets cut off. */}
                      {creatorLabel && (
                        <>
                          <span className={cs.runCreator}>{creatorLabel}</span>
                          <span>{' · '}</span>
                        </>
                      )}
                      <span className={cs.runTimestamp}>
                        {moment(run.startedAt).format('MMM D, YYYY · h:mm A')}
                      </span>
                    </div>
                  </div>
                  {run.status === 'completed' ? (
                    <B.Tag minimal round className={cs.statusTag}>
                      {getObservationCount(run)}{' '}
                      {getObservationCount(run) === 1 ? 'observation' : 'observations'}
                    </B.Tag>
                  ) : run.status === 'failed' ? (
                    <B.Tag minimal round intent="danger" className={cs.statusTag}>
                      Failed
                    </B.Tag>
                  ) : run.status === 'cancelled' ? (
                    <B.Tag minimal round className={cs.statusTag}>
                      Cancelled
                    </B.Tag>
                  ) : (
                    <B.Spinner size={16} className={cs.statusTag} />
                  )}
                  <B.Button
                    icon="trash"
                    minimal
                    small
                    className={cs.archiveButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm('Are you sure you want to archive this run?')) {
                        archiveRun.mutate({agentRunId: run.id});
                      }
                    }}
                    disabled={isArchiving}
                  />
                </div>
              );
            })
          ))}
      </div>
      {totalPages > 1 && (
        <B.ButtonGroup minimal fill className={cs.pagination}>
          <B.Button
            icon="chevron-left"
            disabled={effectivePage <= 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          />
          <B.Button disabled className={cs.pageIndicator}>
            Page {effectivePage} of {totalPages}
          </B.Button>
          <B.Button
            icon="chevron-right"
            disabled={effectivePage >= totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          />
        </B.ButtonGroup>
      )}
    </div>
  );
};

export default RunsView;
